import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Download, Copy, Check, FileCode, ArrowLeft } from 'lucide-react'
import { useGenerationStore } from '@/stores/generationStore'

export default function PreviewPage() {
  const { generatedCode, downloadZip } = useGenerationStore()
  const [selectedFile, setSelectedFile] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  if (!generatedCode) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card text-center py-12">
          <FileCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">生成されたコードがありません</h2>
          <p className="text-gray-500 mb-6">サービスを選択してコードを生成してください</p>
          <Link to="/generator" className="btn btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            ジェネレータに戻る
          </Link>
        </div>
      </div>
    )
  }

  const files = generatedCode.files
  const currentFile = selectedFile
    ? files.find((f) => f.path === selectedFile)
    : files[0]

  const handleCopy = async () => {
    if (currentFile) {
      await navigator.clipboard.writeText(currentFile.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Generated Terraform Code</h1>
        <div className="flex gap-4">
          <Link to="/generator" className="btn btn-outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            戻る
          </Link>
          <button onClick={downloadZip} className="btn btn-primary">
            <Download className="w-4 h-4 mr-2" />
            ZIPダウンロード
          </button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* File Tree */}
        <div className="lg:w-1/4">
          <div className="card">
            <h3 className="font-semibold text-gray-900 mb-4">Files</h3>
            <div className="space-y-1">
              {files.map((file) => (
                <button
                  key={file.path}
                  onClick={() => setSelectedFile(file.path)}
                  className={`w-full text-left px-3 py-2 rounded text-sm flex items-center gap-2 ${
                    (selectedFile || files[0]?.path) === file.path
                      ? 'bg-orange-100 text-orange-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FileCode className="w-4 h-4" />
                  {file.path}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Code Preview */}
        <div className="lg:w-3/4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{currentFile?.path}</h3>
              <button
                onClick={handleCopy}
                className="btn btn-outline text-sm flex items-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-green-500" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm leading-relaxed">
              <code>{currentFile?.content}</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  )
}
